import { useQuery } from '@tanstack/react-query'
import { TvChannel, TvProgramme, TvData } from '../types/tvTypes'
import { supabase } from '@/lib/supabase'
import { getCanonicalIdsForFeedId } from '@/utils/channelIdAliases'
import { applySignedTitleDetection } from '@/utils/signedContent'
import { getTvListingsDays } from '@/config/tvListings'
import {
  APP_SETTINGS_TV_LISTINGS_DAYS_KEY,
  APP_SETTINGS_SIGNED_TITLE_KEY,
  APP_SETTINGS_LISTINGS_SHOW_SIGNED_PROGRAMS_KEY,
} from '@/config/appSettingsKeys'

export const TV_DATA_QUERY_KEY = ['tv-data'] as const

/** DB row from tv_listings (subset we need). */
interface TvListingRow {
  channel_id: string
  start_at: string
  end_at: string | null
  title: string | null
  description: string | null
  icon_url: string | null
  has_ad: boolean
  has_subtitles: boolean
  has_bsl: boolean
}

/** Displayed channel from listing_channel_display. */
interface DisplayedChannelRow {
  channel_id: string
  name: string
  icon_url: string | null
}

/** Global app settings we need for TV data (listing days + signed-title preference + show-signed flag). */
const TV_DATA_APP_SETTINGS_KEYS = [
  APP_SETTINGS_TV_LISTINGS_DAYS_KEY,
  APP_SETTINGS_SIGNED_TITLE_KEY,
  APP_SETTINGS_LISTINGS_SHOW_SIGNED_PROGRAMS_KEY,
] as const

/** Fetch listing duration, signed-title preference, and show-signed-in-listings flag from app_settings in one call. */
async function fetchTvAppSettings(): Promise<{
  listingDays: 7 | 14
  includeSignedTitle: boolean
  showSignedInListings: boolean
}> {
  const { data, error } = await supabase
    .from('app_settings')
    .select('key, value')
    .in('key', [...TV_DATA_APP_SETTINGS_KEYS])
  if (error) {
    return { listingDays: getTvListingsDays(), includeSignedTitle: false, showSignedInListings: true }
  }
  const map = new Map((data ?? []).map((r) => [r.key, r.value]))
  const daysVal = map.get(APP_SETTINGS_TV_LISTINGS_DAYS_KEY)
  const listingDays: 7 | 14 = daysVal === '7' || daysVal === '14' ? daysVal : getTvListingsDays()
  const includeSignedTitle = map.get(APP_SETTINGS_SIGNED_TITLE_KEY) === 'true'
  const showSignedInListings = map.get(APP_SETTINGS_LISTINGS_SHOW_SIGNED_PROGRAMS_KEY) !== 'false'
  return { listingDays, includeSignedTitle, showSignedInListings }
}

/** Start of today UTC and start of (today + N days) UTC for filtering listings. */
function getListingsWindowForDays(days: number): { start: Date; end: Date } {
  const now = new Date()
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0, 0))
  const end = new Date(start)
  end.setUTCDate(end.getUTCDate() + days)
  return { start, end }
}

/** Load TV data from DB (tv_listings + listing_channel_display). Phase 3 primary path. */
async function loadTvDataFromDb(
  days: number,
  includeSignedTitle: boolean,
  showSignedInListings: boolean
): Promise<TvData> {
  const { data: channelRows, error: channelError } = await supabase
    .from('listing_channel_display')
    .select('channel_id, name, icon_url')
    .eq('is_displayed', true)
  if (channelError || !channelRows?.length) {
    return { channels: [], programmes: [] }
  }

  const channels: TvChannel[] = channelRows.map((r: DisplayedChannelRow) => ({
    id: r.channel_id,
    name: r.name,
    icon: r.icon_url ?? undefined,
  }))

  const effectiveDays = Math.min(days, 7)
  const { start: windowStart, end: windowEnd } = getListingsWindowForDays(effectiveDays)
  const windowStartIso = windowStart.toISOString()
  const windowEndIso = windowEnd.toISOString()

  // Read all rows in the 7-day window, then map to displayed channels in-memory.
  // This avoids missing valid rows when channel_id casing/format differs from strict DB IN filter values.
  const PAGE = 1000
  const listingRows: TvListingRow[] = []
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await supabase
      .from('tv_listings')
      .select('channel_id, start_at, end_at, title, description, icon_url, has_ad, has_subtitles, has_bsl')
      .gte('start_at', windowStartIso)
      .lt('start_at', windowEndIso)
      .order('start_at', { ascending: true })
      .range(from, from + PAGE - 1)
    if (error) {
      console.error('Failed to fetch tv_listings:', error)
      return { channels, programmes: [] }
    }
    const batch = (data ?? []) as TvListingRow[]
    if (batch.length === 0) break
    listingRows.push(...batch)
    if (batch.length < PAGE) break
  }

  const displayedMatchers = channelRows.map((ch) => {
    const canonicals = getCanonicalIdsForFeedId(ch.channel_id)
    const canonicalSet = new Set((canonicals.length ? canonicals : [ch.channel_id]).map((v) => v.toLowerCase()))
    return { feedId: ch.channel_id, canonicalSet }
  })

  const programmes: TvProgramme[] = []
  for (const row of listingRows) {
    const rowChannelLower = row.channel_id.toLowerCase()
    const start = new Date(row.start_at)
    const stop = row.end_at ? new Date(row.end_at) : new Date(start.getTime() + 3600000)
    for (const ch of displayedMatchers) {
      const matches = ch.canonicalSet.has(rowChannelLower)
      if (!matches) continue
      programmes.push({
        channelId: ch.feedId,
        start,
        stop,
        title: row.title ?? '',
        desc: row.description ?? undefined,
        icon: row.icon_url ?? undefined,
        has_ad: row.has_ad,
        has_subtitles: row.has_subtitles,
        has_bsl: row.has_bsl,
      })
    }
  }

  programmes.sort((a, b) => {
    if (a.channelId !== b.channelId) return a.channelId.localeCompare(b.channelId)
    return a.start.getTime() - b.start.getTime()
  })

  const result: TvData = { channels, programmes }
  if (showSignedInListings && includeSignedTitle) {
    applySignedTitleDetection(result)
  }
  return result
}

export type TvDataWithListingDays = TvData & { listingDays: number }

/** Phase 3: Load TV data from tv_listings (DB-backed). No XML fetch or parse. */
async function loadTvData(): Promise<TvDataWithListingDays> {
  const { listingDays: days, includeSignedTitle, showSignedInListings } = await fetchTvAppSettings()
  const result = await loadTvDataFromDb(days, includeSignedTitle, showSignedInListings)
  const effectiveListingDays = Math.min(days, 7)
  return { ...result, listingDays: effectiveListingDays }
}

export function useTvData() {
  const { data, isLoading, error } = useQuery({
    queryKey: TV_DATA_QUERY_KEY,
    queryFn: loadTvData,
    staleTime: 60_000,
  })
  return {
    data: data ? { channels: data.channels, programmes: data.programmes } : null,
    listingDays: data?.listingDays ?? 7,
    loading: isLoading,
    error: error ? (error instanceof Error ? error.message : 'Failed to load TV schedule.') : null,
  }
}
